import AllBrandsSearch from "src/organisms/AllBrandsSearch";
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next";
import { HomeView } from "src/organisms/HomeView";
import getConfig from "next/config";
import { Homepage, NavigationItems, ShopType } from "src/types/graphql.d";
import { getHomepage } from "services/graphql/get-homepage";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { getNavigationItems } from "services/graphql/get-navigation-items";

type HomePageProps = {
  pageProps:
    | {
        isAllBrands: true;
        slug: string;
        gender: string;
        locale: string;
        navigationItems?: NavigationItems | null;
      }
    | {
        isAllBrands: false;
        homepage: Homepage;
        slug: string;
        locale: string;
        navigationItems?: NavigationItems | null;
        gender: string;
      };
};

const { serverRuntimeConfig } = getConfig();

const genderTranslations = [
  "women",
  "femme",
  "damen",
  "dames",
  "kobiety",
  "mujer",
  "donna",
  "dame",
  "kvinna",
  "kids",
  "enfant",
  "kinder",
  "kids",
  "ninos",
  "bambini",
  "dzieci",
  "born",
  "barn",
  "men",
  "harren",
  "herren",
  "homme",
  "heren",
  "hombre",
  "uomo",
  "man",
  "mezczyzni",
];

const allBrandsTranslations = [
  "brands",
  "marques",
  "marken",
  "merken",
  "marki",
  "marcas",
  "marche",
  "brand",
  "maerker",
  "varumarken",
];

function HomePage({ pageProps }: HomePageProps): JSX.Element {
  // Route for all "/brands"
  if (pageProps.isAllBrands) {
    return (
      <AllBrandsSearch genderId={pageProps.slug} locale={pageProps.locale} />
    );
  }

  return <HomeView homepage={pageProps.homepage} />;
}

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{
  slug: string;
}>): Promise<GetStaticPropsResult<HomePageProps["pageProps"]>> {
  if (!params || !params.slug) {
    return {
      notFound: true,
    };
  }

  const isAllBrands = allBrandsTranslations.includes(params.slug);
  const isValidGender = genderTranslations.includes(params.slug);

  if ((!isAllBrands && !isValidGender) || !isValidGender) {
    return {
      notFound: true,
    };
  }

  const { navigationItems } =
    (await getNavigationItems(
      localeFix({ gender: params.slug }) as ShopType
    )) || [];

  if (isAllBrands) {
    return {
      revalidate: 60 * 30,
      props: {
        isAllBrands,
        slug: params.slug,
        gender: params.slug,
        locale: serverRuntimeConfig.LOCALE,
        navigationItems,
      },
    };
  }

  const homepage = await getHomepage({
    gender: localeFix({ gender: params.slug }),
  });

  if (!homepage) {
    return {
      notFound: true,
    };
  }

  return {
    revalidate: 60 * 30,
    props: {
      isAllBrands,
      slug: params.slug,
      locale: serverRuntimeConfig.LOCALE,
      navigationItems,
      homepage: homepage || null,
      gender: params.slug,
    },
  };
}

export default HomePage;
